/**
 * **Signing in with Google or Microsoft comes back where the person was, and says why when it did not work** (Build
 * Queue row 14).
 *
 * The provider sends the person back to the address Autho gave it, and Supabase lets that address be only one of a
 * list of exact URLs (`additional_redirect_urls`): an address with a path in it is not on the list and is replaced by
 * the site's own. So the address handed over stays the site's origin, and the page the person was on is kept in this
 * tab (`sessionStorage`) until they are back, then opened. Only one of the app's own pages ({@link KEEPABLE}), as a
 * path alone, is ever kept or opened: a stored value that is anything else is dropped.
 *
 * A refusal comes back as `error` (and `error_description`) in the query or the fragment; it is read once, when this
 * module loads, before Supabase's client reads and clears the address.
 */

export type OAuthProvider = 'google' | 'azure';

const KEY = 'autho.signIn.return';

interface Kept {
  readonly provider: OAuthProvider;
  readonly path: string;
}

/**
 * **The pages a sign-in may come back to**, and nothing else (the Mac's review of acddedf, 2026-11-01: a provider's
 * return address, `/connect/google/callback?code=…`, was kept with its code, and the code lives in memory only, by the
 * rule of 2026-09-22). Only the app's own pages, by a strict pattern per page, are kept: a path, never a query or a
 * fragment. A callback (`/connect/…/callback`), an eID return (`/eid/…/return`), the agent door (`/connect/agent`) and
 * a Sign in with Autho consent screen, which F548 returns to by its own rule, are never kept, so a code or a state in
 * their addresses never reaches storage.
 */
const ID = '[A-Za-z0-9_-]{1,128}';
export const KEEPABLE: readonly RegExp[] = [
  '/',
  '/structure',
  `/structure/${ID}`,
  `/structure/${ID}/ledger`,
  '/beliefs',
  '/chat',
  '/documents',
  '/counterparties',
  '/bank',
  '/accounts',
  '/attention',
  '/relationships',
  `/relationships/${ID}`,
  '/profile',
  '/goals',
  '/finances',
  '/conversations',
  '/calendar',
  '/files',
  '/clarify',
  '/audit',
  '/configure',
  '/configure/agents',
  '/approvals',
  '/settings',
].map((page) => new RegExp(`^${page}$`));

/** One of the app's own pages, as a path alone, or null: no query, no fragment, and nothing off {@link KEEPABLE}. */
export function keepablePath(path: unknown): string | null {
  if (typeof path !== 'string') return null;
  return KEEPABLE.some((page) => page.test(path)) ? path : null;
}

function storage(): Storage | null {
  try {
    return typeof window === 'undefined' ? null : window.sessionStorage;
  } catch {
    // A private window may refuse storage; the person then lands on their profile's first page, as before.
    return null;
  }
}

/** Kept before the provider is asked: which one, and the page the person was on when it is one of the app's own. */
export function rememberSignInReturn(provider: OAuthProvider, path: string, store = storage()) {
  const safe = keepablePath(path);
  if (!store || safe === null) return;
  try {
    store.setItem(KEY, JSON.stringify({ provider, path: safe } satisfies Kept));
  } catch {
    // Full or refused: nothing is kept, and nothing is opened on the way back.
  }
}

function read(store: Storage | null): Kept | null {
  if (!store) return null;
  try {
    const raw = store.getItem(KEY);
    if (raw === null) return null;
    const parsed = JSON.parse(raw) as Partial<Kept>;
    const path = keepablePath(parsed.path);
    const provider =
      parsed.provider === 'google' || parsed.provider === 'azure' ? parsed.provider : null;
    return path !== null && provider !== null ? { provider, path } : null;
  } catch {
    return null;
  }
}

function forget(store: Storage | null) {
  try {
    store?.removeItem(KEY);
  } catch {
    // Nothing to do: the next sign-in writes over it.
  }
}

/** The page to open now that the person is signed in, once: read and forgotten. */
export function takeSignInReturn(store = storage()): string | null {
  const kept = read(store);
  forget(store);
  return kept?.path ?? null;
}

/**
 * **Whether the kept page opens**, decided once both returns are known (row 14 beside F548): never while a Sign in
 * with Autho consent screen waits to be gone back to, or when the person is on one, and never the page they are on.
 */
export function pageToOpen(
  kept: string | null,
  at: { readonly consentWaiting: boolean; readonly onConsent: boolean; readonly here: string },
): string | null {
  if (kept === null || at.consentWaiting || at.onConsent || kept === at.here) return null;
  return kept;
}

/** What a provider's refusal says: `denied` when the person said no, `failed` for anything else. */
export interface Refusal {
  readonly kind: 'denied' | 'failed';
  /** The provider asked, when this tab remembers asking it. */
  readonly provider: OAuthProvider | null;
}

/** A refusal in an address's query or fragment, or null. */
export function refusalIn(search: string, hash: string): 'denied' | 'failed' | null {
  for (const part of [search, hash.replace(/^#/, '?')]) {
    const error = new URLSearchParams(part).get('error');
    if (error) return error === 'access_denied' ? 'denied' : 'failed';
  }
  return null;
}

const AT_LOAD =
  typeof window === 'undefined' ? null : refusalIn(window.location.search, window.location.hash);

/**
 * The refusal the address carried when the app loaded, said once: the provider it names is the one this tab asked,
 * and the page kept for the way back is forgotten, since nobody is signed in to see it.
 */
export function takeRefusal(
  store = storage(),
  atLoad: 'denied' | 'failed' | null = AT_LOAD,
): Refusal | null {
  if (atLoad === null) return null;
  const kept = read(store);
  forget(store);
  return { kind: atLoad, provider: kept?.provider ?? null };
}

let said: { refusal: Refusal | null } | null = null;
/** {@link takeRefusal} for the app: worked out once, so a second render reads the same answer. */
export function refusalOnce(): Refusal | null {
  said ??= { refusal: takeRefusal() };
  return said.refusal;
}
